import React, { useState, useEffect, useContext, useRef, useMemo } from "react";
import Box from "@mui/material/Box";
import {
  Avatar,
  IconButton,
  CircularProgress,
  Typography,
} from "@mui/material";
import { PhotoCamera } from "@mui/icons-material";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { AuthContext, db, storage } from "../../firebase";
import { doc, onSnapshot, setDoc } from "firebase/firestore";
import { getAncestors } from "../../utils/familyTreeUtils";
import { familyTreeData } from "./family";
import PersonDetailCard from "../../components/PersonDetailCard";
import {
  AncestorTree,
  AncestorTreeCallbacks,
  AncestorTreeUIControls,
} from "@ryandymock/ancestor-tree";
import { FamilyMember } from "../../utils/familyTreeUtils";
import { useAuth } from "../../firebase";
import { useSnackbar } from "notistack";
import {
  calculateOverallProgress,
  migrateFamilyTreeData,
  updateMemberProgress,
} from "../../utils/familyTreeProgressUtils";
import { FamilyTreeProgress } from "../../utils/familyTreeUtils";
import {
  checkFamilyTreeOrderCompletion,
  updateOrderCompletionStatus,
  OrderCompletionStatus,
} from "../../utils/orderCompletionUtils";
import { collection, query, where, getDocs } from "firebase/firestore";
import GenerationReadinessView from "../../components/GenerationReadinessView";
import GuidedPersonNavigator from "../../components/GuidedPersonNavigator";
import { buildGuidedEntryList } from "../../utils/guidedEntryUtils";
import { APP_CONFIG } from "../../config/appConfig";

// Lazy so the FamilySearch SDK chain (firebase/functions) only loads when the
// feature flag is enabled.
const FamilySearchConnect = React.lazy(
  () => import("../../components/FamilySearchConnect")
);

// Define types that match the package's internal types
interface Person {
  id: string;
  name: string;
  birth?: string;
  death?: string;
  imageUrl?: string;
  spouseId?: string;
  parentIds?: [string?, string?];
}

type PeopleIndex = Record<string, Person>;

// Helper function to convert hierarchical family data to flat PeopleIndex format
const convertFamilyDataToPeopleIndex = (
  familyData: FamilyMember[]
): PeopleIndex => {
  const people: PeopleIndex = {};

  const visited = new Set<string>();

  const traverse = (node: FamilyMember | undefined | null) => {
    // Guard against null / undefined entries
    if (!node) return;

    if (visited.has(node.id)) return; // avoid redundant processing
    visited.add(node.id);

    // Get parents using backward-compatible helper
    const parents = node.parents || node.children || []; // Backward compatibility
    const parentIds: [string | undefined, string | undefined] = [
      undefined,
      undefined,
    ];
    if (Array.isArray(parents)) {
      if (parents[0]) parentIds[0] = parents[0].id;
      if (parents[1]) parentIds[1] = parents[1].id;
    }

    people[node.id] = {
      id: node.id,
      name: node.name,
      birth: node.birthdate || "",
      death: node.deathdate || "",
      parentIds: parentIds[0] || parentIds[1] ? parentIds : undefined,
      spouseId: (node as any)?.spouseId as string | undefined,
      imageUrl: node.imageMetadata,
    };

    // Recurse upward to parents, skipping any null / undefined entries
    if (Array.isArray(parents)) {
      parents.filter(Boolean).forEach((parent) => traverse(parent));
    }
  };

  familyData.forEach((root) => traverse(root));

  // Second pass: infer spouseId when missing but partner references current id
  Object.values(people).forEach((person) => {
    if (!person.spouseId) {
      const partner = Object.values(people).find(
        (p) => p.spouseId === person.id
      );
      if (partner) {
        person.spouseId = partner.id;
      }
    }
  });

  // Third pass: remove any spouseId that does not map to a valid person entry
  Object.values(people).forEach((person) => {
    if (person.spouseId && !people[person.spouseId]) {
      delete person.spouseId;
    }
  });

  return people;
};

const findFamilyMember = (
  memberId: string,
  familyData: FamilyMember[]
): FamilyMember | null => {
  for (const member of familyData) {
    if (member.id === memberId) {
      return member;
    }
    const parents = member.parents || member.children || []; // Backward compatibility
    if (parents.length > 0) {
      const found = findFamilyMember(memberId, parents);
      if (found) {
        return found;
      }
    }
  }
  return null;
};

const FamilyTreeTab: React.FC = () => {
  const [familyTree, setFamilyTree] = useState<FamilyMember[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [selectedPersonId, setSelectedPersonId] = useState<string | null>(null);
  const selectedPersonRef = useRef<string | null>(null);

  // Keep ref in sync with state
  useEffect(() => {
    selectedPersonRef.current = selectedPersonId;
  }, [selectedPersonId]);

  // Set selected person ID and keep ref in sync
  const setSelectedPersonIdWithLogging = (newId: string | null) => {
    setSelectedPersonId(newId);
    selectedPersonRef.current = newId;
  };
  const [hasInitialized, setHasInitialized] = useState(false);
  const [progress, setProgress] = useState<FamilyTreeProgress | null>(null);


  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  // Function to check and update order completion status
  const checkAndUpdateOrderCompletion = async (
    newProgress: FamilyTreeProgress
  ) => {
    if (!user) return;

    try {
      // Get user's active orders that require family tree completion
      const ordersRef = collection(db, "orders");
      const activeOrdersQuery = query(
        ordersRef,
        where("userId", "==", user.uid),
        // identifying "active" orders broadly to catch those that might need status updates
        where("status", "in", ["pending", "awaiting-info", "in-progress"])
      );

      const ordersSnapshot = await getDocs(activeOrdersQuery);

      // Use for...of to correctly await async operations
      for (const orderDoc of ordersSnapshot.docs) {
        const orderData = orderDoc.data();
        const orderId = orderDoc.id;

        // Check if this order requires family tree completion
        const requiredSteps = orderData.items?.[0]?.requiredForms || [];
        const productId = orderData.items?.[0]?.productId;
        const selectedOptions = orderData.items?.[0]?.selectedOptions || {};

        if (
          requiredSteps.includes("FamilyTree") ||
          productId?.includes("pft") ||
          productId?.includes("ga")
        ) {
          // Check completion status
          const completionStatus = checkFamilyTreeOrderCompletion(
            newProgress,
            productId,
            selectedOptions
          );

          // Update order status if needed
          const result = await updateOrderCompletionStatus(
            orderId,
            completionStatus,
            orderData.status,
            newProgress,
            orderData // Pass existing data optimization
          );

          // Show notification ONLY if order status CHANGED to "in-progress" (ready)
          if (result.success && result.statusChanged && result.newStatus === "in-progress") {
            enqueueSnackbar(
              "🎉 Your order is now complete and ready for processing!",
              {
                variant: "success",
                autoHideDuration: 6000,
              }
            );
          }
        }
      }
    } catch (error) {
      console.error("Error checking order completion:", error);
    }
  };

  // Convert family tree data to the format expected by AncestorTree
  const peopleIndex = convertFamilyDataToPeopleIndex(familyTree);

  // Ordered, labeled entry checklist (You → Parents → Grandparents → …)
  const guidedItems = useMemo(() => buildGuidedEntryList(familyTree), [familyTree]);
  const selectedGuidedItem = guidedItems.find(
    (item) => item.member.id === selectedPersonId
  );

  // On phones the editor sits below the tree; bring it into view on selection
  const editorRef = useRef<HTMLDivElement | null>(null);
  const scrollEditorIntoView = () => {
    if (window.innerWidth < 1200 && editorRef.current) {
      editorRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  // Define callbacks for AncestorTree interactions
  const callbacks: AncestorTreeCallbacks = {
    onPersonClick: (person: Person) => {
      setSelectedPersonIdWithLogging(person.id);
      scrollEditorIntoView();
    },
    onViewportChange: (x: number, y: number, zoom: number) => {
      // Handle viewport change if needed
    },
    onTreePan: (x: number, y: number) => {
      // Handle tree pan if needed
    },
    onTreeZoom: (zoom: number) => {
      // Handle tree zoom if needed
    },
    onCoupleExpansion: (
      partner1: Person | undefined,
      partner2: Person | undefined,
      isExpanded: boolean
    ) => {
      // Handle couple expansion if needed
    },
  };

  // Helper function to create template-based subtitle formatter
  const createSubtitleFormatter = (template: string) => {
    return (person: Person) => {
      // Helper function to format dates
      const formatDate = (dateString?: string, format?: string) => {
        if (
          !dateString ||
          dateString === "YYYY-MM-DD" ||
          dateString.trim() === ""
        )
          return "";

        try {
          const date = new Date(dateString);
          if (isNaN(date.getTime())) return "";

          // Simple date formatting - you can expand this based on needs
          if (format === "yyyy") {
            return date.getFullYear().toString();
          }
          // Add more format options as needed
          return date.getFullYear().toString();
        } catch {
          return "";
        }
      };

      // Calculate lifespan
      const getLifespan = (person: Person) => {
        const birthYear = person.birth ? formatDate(person.birth, "yyyy") : "";
        const deathYear = person.death ? formatDate(person.death, "yyyy") : "";

        if (birthYear && deathYear) {
          return `${birthYear}-${deathYear}`;
        } else if (birthYear) {
          return `${birthYear}-`;
        }
        return "";
      };

      // Replace template variables
      return template
        .replace(/{name}/g, person.name || "")
        .replace(/{birth}/g, person.birth || "")
        .replace(/{death}/g, person.death || "")
        .replace(/{id}/g, person.id || "")
        .replace(
          /{birthYear}/g,
          person.birth ? formatDate(person.birth, "yyyy") : ""
        )
        .replace(
          /{deathYear}/g,
          person.death ? formatDate(person.death, "yyyy") : ""
        )
        .replace(/{lifespan}/g, getLifespan(person));
    };
  };

  // Configure UI controls for AncestorTree
  const uiControls: AncestorTreeUIControls = {
    showControls: true,
    showMiniMap: false,
    showBackground: true,
    enablePan: true,
    enableZoom: true,
    enableFitView: true,
    backgroundColor: "#fafafa",
    // Custom spacing to fix MUI theme card overlapping
    nodeHeight: 140,
    verticalGaps: [0, 450, 200, 400, 150],
    defaultVerticalGap: 75,
    // Custom subtitle formatting to show lifespan
    formatPersonSubtitle: createSubtitleFormatter("{lifespan}"),
  };

  // useEffect to track familyTree state variable changes
  useEffect(() => {
    // Calculate progress whenever family tree changes
    if (familyTree.length > 0) {
      const newProgress = calculateOverallProgress(familyTree);
      setProgress(newProgress);

      // Check and update order completion status
      checkAndUpdateOrderCompletion(newProgress);
    }
  }, [familyTree, user]);

  useEffect(() => {
    const checkAndCreateDefaultFamilyTree = async () => {
      if (!user) {
        return;
      }

      const userDocRef = doc(db, "users", user.uid);

      setLoading(true);

      const unsubscribe = onSnapshot(
        userDocRef,
        async (docSnapshot) => {
          if (docSnapshot.exists()) {
            const data = docSnapshot.data();
            // If the field is completely absent or falsy, write the default array.
            if (!Array.isArray(data.familyTreeData)) {
              // Only initialize with default data if we haven't already done so
              if (!hasInitialized) {
                const initial: FamilyMember[] = familyTreeData; // or use []
                await setDoc(
                  userDocRef,
                  { familyTreeData: initial },
                  { merge: true }
                );
                setHasInitialized(true);
              }
              setFamilyTree(familyTreeData);
              if (!selectedPersonId && familyTreeData.length > 0) {
                setSelectedPersonIdWithLogging(familyTreeData[0].id);
              }
            } else {
              // Load existing data from Firestore
              const currentSelectedId =
                selectedPersonRef.current || selectedPersonId;

              // Migrate existing data to include progress tracking fields
              const migratedData = migrateFamilyTreeData(data.familyTreeData);

              setFamilyTree(migratedData);

              // Save migrated data back to Firestore if migration occurred
              const needsMigration = data.familyTreeData.some(
                (member: any) => member.editStatus === undefined
              );
              if (needsMigration) {
                await setDoc(
                  userDocRef,
                  { familyTreeData: migratedData },
                  { merge: true }
                );
              }

              // Preserve selection during updates - only change selection if no one is selected
              if (!currentSelectedId && migratedData.length > 0) {
                setSelectedPersonIdWithLogging(migratedData[0].id);
              } else if (currentSelectedId) {
                // Always preserve selection if someone is selected
                // Ensure the ref and state are in sync
                if (selectedPersonRef.current !== selectedPersonId) {
                  setSelectedPersonId(currentSelectedId);
                }
              }
            }
          } else {
            // Document doesn't exist at all: create it with a default array
            const initial: FamilyMember[] = familyTreeData; // or use []
            await setDoc(
              userDocRef,
              { familyTreeData: initial },
              { merge: true }
            );
            setFamilyTree(initial);
            if (!selectedPersonId && initial.length > 0) {
              setSelectedPersonIdWithLogging(initial[0].id);
            }
          }
          setLoading(false);
        },
        (error) => {
          console.error("Snapshot error:", error);
          setLoading(false);
        }
      );

      return () => unsubscribe();
    };

    checkAndCreateDefaultFamilyTree();
  }, [user]); // Only depend on user, not selectedPersonId

  // Recursive function to update the family member in the tree
  const updateFamilyMember = (
    members: FamilyMember[],
    id: string,
    updatedData: Partial<FamilyMember>
  ): FamilyMember[] => {
    return members.map((member) => {
      if (member.id === id) {
        return { ...member, ...updatedData };
      }
      const parents = member.parents || member.children || []; // Backward compatibility
      if (parents.length > 0) {
        const updatedParents = updateFamilyMember(parents, id, updatedData);
        const updatedMember: FamilyMember = {
          ...member,
        };

        // Only set parents if they exist, don't set undefined
        if (member.parents) {
          updatedMember.parents = updatedParents;
        }

        // Only set children if they exist AND there are no parents (backward compatibility)
        if (member.children && !member.parents) {
          updatedMember.children = updatedParents;
        }

        return updatedMember;
      }
      return member;
    });
  };

  // Function to detect undefined values in data structure
  const findUndefinedValues = (data: any, path: string = ""): string[] => {
    const undefinedPaths: string[] = [];

    if (Array.isArray(data)) {
      data.forEach((item, index) => {
        undefinedPaths.push(...findUndefinedValues(item, `${path}[${index}]`));
      });
    } else if (data && typeof data === "object") {
      Object.keys(data).forEach((key) => {
        const currentPath = path ? `${path}.${key}` : key;
        if (data[key] === undefined) {
          undefinedPaths.push(currentPath);
        } else {
          undefinedPaths.push(...findUndefinedValues(data[key], currentPath));
        }
      });
    }

    return undefinedPaths;
  };

  // Function to clean undefined values from objects before Firestore save
  const cleanUndefinedValues = (data: any): any => {
    if (Array.isArray(data)) {
      return data.map(cleanUndefinedValues);
    } else if (data && typeof data === "object") {
      const cleaned: any = {};
      Object.keys(data).forEach((key) => {
        if (data[key] !== undefined) {
          cleaned[key] = cleanUndefinedValues(data[key]);
        }
      });
      return cleaned;
    }
    return data;
  };

  const handleFamilySearchImport = async (importedTree: FamilyMember[]) => {
    if (!user) return;
    setSaving(true);
    try {
      const cleaned = cleanUndefinedValues(importedTree);
      setFamilyTree(cleaned);
      const userDocRef = doc(db, "users", user.uid);
      await setDoc(userDocRef, { familyTreeData: cleaned }, { merge: true });
      enqueueSnackbar("Family tree imported from FamilySearch!", { variant: "success" });
    } catch (err) {
      enqueueSnackbar("Failed to save imported tree.", { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>

      {/* Readiness Indicators + FamilySearch Connect */}
      <Box sx={{ px: 2, pt: 2, display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
        <GenerationReadinessView generationProgress={progress?.generationProgress} />
        {APP_CONFIG.FEATURES.FAMILYSEARCH && (
          <React.Suspense fallback={null}>
            <FamilySearchConnect onImport={handleFamilySearchImport} />
          </React.Suspense>
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", lg: "row" },
          padding: { xs: 1, sm: 2 },
          gap: 2,
        }}
      >
        <Box sx={{ flex: 1, overflow: "hidden", minWidth: 0 }}>
          <Box sx={{ width: "100%", height: { xs: 380, sm: 480, lg: 600 } }}>
            <AncestorTree
              people={peopleIndex}
              rootId={familyTree.length > 0 ? familyTree[0].id : "self"}
              callbacks={callbacks}
              uiControls={uiControls}
            />
          </Box>
        </Box>
        <Box
          ref={editorRef}
          sx={{ width: { xs: "100%", lg: 560 }, flexShrink: 0 }}
        >
          <GuidedPersonNavigator
            items={guidedItems}
            selectedId={selectedPersonId}
            onSelect={(id) => {
              setSelectedPersonIdWithLogging(id);
            }}
          />
          {loading ? (
            <CircularProgress />
          ) : (
            selectedPersonId && (
              <PersonDetailCard
                member={findFamilyMember(selectedPersonId, familyTree)!}
                relationshipLabel={selectedGuidedItem?.relationshipLabel}
                parentOfLabel={selectedGuidedItem?.parentOfLabel ?? undefined}
                onChange={async (updated) => {
                  try {
                    setSaving(true);

                    // Update progress tracking fields for the modified member
                    const updatedWithProgress = updateMemberProgress(updated);

                    // Update the family tree with the new data
                    const updatedTree = updateFamilyMember(
                      familyTree,
                      updatedWithProgress.id,
                      updatedWithProgress
                    );

                    // Check for undefined values before cleaning
                    const undefinedPaths = findUndefinedValues(updatedTree);
                    if (undefinedPaths.length > 0) {
                      console.warn(
                        `🚨 Found undefined values at paths:`,
                        undefinedPaths
                      );
                      console.warn(
                        `🔍 Full updatedTree object:`,
                        JSON.stringify(updatedTree, null, 2)
                      );
                    }

                    // Clean undefined values from the tree before saving to Firestore
                    const cleanedTree = cleanUndefinedValues(updatedTree);

                    // Update local state immediately for responsive UI
                    setFamilyTree(cleanedTree);

                    // Save to Firestore (this will trigger onSnapshot but selection will be preserved)
                    const userDocRef = doc(db, "users", user.uid);
                    await setDoc(
                      userDocRef,
                      { familyTreeData: cleanedTree },
                      { merge: true }
                    );

                    // Small delay before resetting saving state to ensure onSnapshot processes
                    await new Promise((resolve) => setTimeout(resolve, 100));
                  } finally {
                    setSaving(false);
                  }
                }}
              />
            )
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default FamilyTreeTab;
